import {
    IProjectNextProjectDetails,
    IProjectProgress,
} from 'holberton-school-intranet-api';
import * as React from 'react';
import { ReactElement } from 'react';

import Icon from '../common/Icon';
import ProgressBar from '../common/ProgressBar';
import { t } from '../../utils/i18n';

import ProjectReadyForReviewBody, {
    IProjectPeerReview,
} from './ProjectReadyForReviewBody';

interface IProps {
    csrfToken: string;
    fullManualReview: boolean;
    hasManualChecks?: boolean;
    loading: boolean;
    nextProjectDetails: IProjectNextProjectDetails | null;
    onStartReview: () => void;
    peerReview: IProjectPeerReview | null;
    progress: IProjectProgress;
    skipURI: string;
    project?: {
        completion?: number;
    };
}

export default function ProjectReviewBody({
    csrfToken,
    fullManualReview,
    hasManualChecks = false,
    loading,
    nextProjectDetails,
    onStartReview,
    peerReview,
    progress,
    skipURI,
    project,
}: IProps): ReactElement {
    /* eslint-disable @typescript-eslint/camelcase */
    const {
        auto_review: {
            can_execute: canExecute,
            completion,
            inability_to_execute_reason: reason,
        },
        can_skip: canSkip,
        can_start_peer_review: canStartPeerReview,
        summary,
    } = progress;
    /* eslint-enable @typescript-eslint/camelcase */

    // Get completion percentage from project or progress summary
    const projectCompletion = project?.completion ?? summary?.completion ?? 0;
    
    // Message
    // Only show "completed all projects" message if:
    // 1. Reason is 'done' or 'full_peer_review'
    // 2. There's a next project message (which contains "completed all projects" when it's the last project)
    // 3. The current project is actually completed (completion === 100%)
    const isCompletedAllProjectsMessage = nextProjectDetails?.message?.includes('completed all the projects') || false;
    const hasDoneMessage =
        (reason === 'done' || reason === 'full_peer_review') &&
        nextProjectDetails?.message &&
        (!isCompletedAllProjectsMessage || projectCompletion >= 100);
    const hasInProgressMessage = reason === 'in_progress';
    const hasManualMessage = reason === 'manual';
    const hasMessage =
        hasDoneMessage || hasInProgressMessage || hasManualMessage;

    // CTA
    // Remove the "Go to home" button - no longer needed
    const hasGoToHomeCTA = false;
    // Remove the "Review all the tasks" button - no longer needed
    const hasLaunchProjectReviewCTA = false;
    const hasOpenNextProjectCTA =
        (canStartPeerReview ||
            reason === 'done' ||
            reason === 'full_peer_review') &&
        !hasInProgressMessage &&
        nextProjectDetails.uri;
    const hasSkipCTA = false; // canSkip && !hasOpenNextProjectCTA;
    const hasStartPeerReviewCTA = canStartPeerReview && !hasInProgressMessage;

    const hasCTA =
        hasGoToHomeCTA ||
        hasOpenNextProjectCTA ||
        hasSkipCTA;

    return (
        <>
            {peerReview && hasManualChecks && (
                <ProjectReadyForReviewBody
                    csrfToken={csrfToken}
                    peerReview={peerReview}
                />
            )}

            {hasMessage && (
                <div>
                    {hasDoneMessage && <p>{nextProjectDetails.message}</p>}

                    {hasInProgressMessage && (
                        <>
                            <p>
                                <Icon
                                    name="refresh"
                                    spin={true}
                                    text={t('projects.review.processing_wait', 'Processing... Please wait...')}
                                />
                            </p>

                            {completion && (
                                <div className="mt-2">
                                    <ProgressBar
                                        value={completion.percentage}
                                    />
                                </div>
                            )}
                        </>
                    )}

                    {hasManualMessage && (
                        <p>
                            {t('projects.review.please_review_all_manual_checks', 'Please review all the')} <strong>{t('projects.review.manual_checks', 'manual checks')}</strong> {t('projects.review.before_launch', 'before you launch the project review.')}</p>
                    )}
                </div>
            )}

            {hasCTA && (
                <div className="d-flex flex-wrap gap-3 justify-content-between mt-4">
                    <div className="btn-group">
                        {hasGoToHomeCTA && (
                            <a className="btn btn-primary" href="/">
                                <Icon name="home" text={t('projects.review.go_to_home', 'Go to home')} />
                            </a>
                        )}

                        {hasLaunchProjectReviewCTA && (
                            <button
                                className="btn btn-primary"
                                disabled={loading}
                                id="project-launch-review-btn"
                                onClick={onStartReview}
                            >
                                <Icon
                                    name={loading ? 'refresh' : 'paper-plane'}
                                    spin={loading}
                                    text={
                                        loading
                                            ? (fullManualReview
                                                ? t('projects.review.preparing_manual_review', 'Preparing your manual review')
                                                : t('projects.review.reviewing_all_tasks', 'Reviewing all the tasks'))
                                            : (fullManualReview
                                                ? t('projects.review.ready_for_review', 'Ready for my review')
                                                : t('projects.review.review_all_tasks', 'Review all the tasks'))
                                    }
                                />
                            </button>
                        )}

                        {hasOpenNextProjectCTA && (
                            <form action={skipURI} method="post">
                                <input
                                    name="authenticity_token"
                                    type="hidden"
                                    value={csrfToken}
                                />
                                <button className="btn btn-primary" type="submit">
                                    <Icon
                                        name="code-fork"
                                        text={t('projects.review.open_next_project', 'Open the next project')}
                                    />
                                </button>
                            </form>
                        )}
                    </div>
                    <div>
                        {hasSkipCTA && (
                            <form action={skipURI} method="post">
                                <input
                                    name="authenticity_token"
                                    type="hidden"
                                    value={csrfToken}
                                />

                                <button
                                    className="btn btn-default"
                                    type="submit"
                                >
                                    <Icon
                                        name="forward"
                                        text={t('projects.review.skip_project', 'Skip this project')}
                                    />
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
